import { useEffect, useRef, useState } from "preact/hooks";
import { Icon } from "./icons";
import type { PDFDocumentProxy, PDFPageProxy } from "./pdf";

interface Props {
  file: Blob;
  name: string;
  onClose: () => void;
  onDownload: () => void;
}

/** The widest a page is shown unzoomed (on a phone it is the screen's width). */
const PAGE_WIDTH = 900;
/** Pages are drawn twice as sharp as the screen shows them unzoomed, so they stay sharp zoomed in to 2. */
const SHARPNESS = 2;
const MAX_ZOOM = 4;

/** A PDF attachment in the app, page under page, drawn by PDF.js as images as they come near the screen: it opens at
 * once, offline too, where the installed iPhone app hands a file it opens in a new tab to a browser sheet that offers
 * it as a download. Pinch zooms (and ctrl + scroll, a trackpad's pinch); the download button saves the file. */
export function PdfViewer({ file, name, onClose, onDownload }: Props) {
  const [sizes, setSizes] = useState<{ width: number; height: number }[] | null>(null);
  /** Each page's image: undefined until drawn, null when it could not be. */
  const [images, setImages] = useState<(string | null | undefined)[]>([]);
  const [failed, setFailed] = useState<string | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const pagesBox = useRef<HTMLDivElement>(null);
  const draw = useRef<(index: number) => void>(() => {});
  const zoom = useRef(1);

  // Open the document, then draw the pages `draw` asks for, one at a time, the first ones first.
  useEffect(() => {
    let live = true;
    let doc: PDFDocumentProxy | null = null;
    const urls: string[] = [];
    (async () => {
      try {
        const { openPdf, pageImage } = await import("./pdf");
        const opened = await openPdf(await file.arrayBuffer());
        if (!live) return void opened.loadingTask.destroy();
        doc = opened;
        const pages: PDFPageProxy[] = [];
        for (let i = 1; i <= doc.numPages; i++) pages.push(await doc.getPage(i));
        if (!live) return;
        const queue = new Set<number>();
        let running = false;
        const run = async () => {
          running = true;
          while (live && queue.size) {
            const index = Math.min(...queue);
            queue.delete(index);
            const width = Math.min(scroller.current?.clientWidth || PAGE_WIDTH, PAGE_WIDTH) * Math.min(devicePixelRatio || 1, 3) * SHARPNESS;
            let url: string | null = null;
            try {
              url = URL.createObjectURL(await pageImage(pages[index], width));
            } catch {
              // shown as a page that could not be drawn (or the viewer was closed while drawing it)
            }
            if (!live) {
              if (url) URL.revokeObjectURL(url);
              break;
            }
            if (url) urls.push(url);
            setImages((now) => {
              const next = now.slice();
              next[index] = url;
              return next;
            });
          }
          running = false;
        };
        const asked = new Set<number>();
        draw.current = (index) => {
          if (asked.has(index)) return;
          asked.add(index);
          queue.add(index);
          if (!running) void run();
        };
        setSizes(pages.map((page) => page.getViewport({ scale: 1 })).map(({ width, height }) => ({ width, height })));
      } catch (e) {
        if (!live) return;
        setFailed(
          e instanceof Error && e.name === "PasswordException"
            ? "This PDF has a password. Download it to open it."
            : "This PDF cannot be shown here. Download it to open it.",
        );
      }
    })();
    return () => {
      live = false;
      void doc?.loadingTask.destroy();
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [file]);

  // Draw the pages on the screen and the next screenful either side.
  useEffect(() => {
    const root = scroller.current;
    const box = pagesBox.current;
    if (!sizes || !root || !box) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && draw.current(Number((e.target as HTMLElement).dataset.page))),
      { root, rootMargin: "100% 0px" },
    );
    for (const page of Array.from(box.children)) observer.observe(page);
    return () => observer.disconnect();
  }, [sizes]);

  // Zoom: the pages' width follows it, and the scroll keeps the point the fingers (or the pointer) hold under them.
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    /** Where client point (x, y) is on the pages, unzoomed. */
    const pointAt = (x: number, y: number) => {
      const box = pagesBox.current!.getBoundingClientRect();
      return { x: (x - box.left) / zoom.current, y: (y - box.top) / zoom.current };
    };
    /** Zoom to `next`, with the pages' `point` at client point (x, y). */
    const zoomTo = (next: number, point: { x: number; y: number }, x: number, y: number) => {
      const pages = pagesBox.current;
      if (!pages) return;
      zoom.current = Math.min(MAX_ZOOM, Math.max(1, next));
      pages.style.setProperty("--zoom", String(zoom.current));
      const box = pages.getBoundingClientRect();
      el.scrollLeft += box.left + point.x * zoom.current - x;
      el.scrollTop += box.top + point.y * zoom.current - y;
    };
    let pinch: { spread: number; zoom: number; point: { x: number; y: number } } | null = null;
    const spread = (t: TouchList) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const middle = (t: TouchList) => ({ x: (t[0].clientX + t[1].clientX) / 2, y: (t[0].clientY + t[1].clientY) / 2 });
    const start = (e: TouchEvent) => {
      if (e.touches.length !== 2 || !pagesBox.current) return;
      const m = middle(e.touches);
      pinch = { spread: spread(e.touches), zoom: zoom.current, point: pointAt(m.x, m.y) };
    };
    const move = (e: TouchEvent) => {
      if (!pinch || e.touches.length !== 2) return;
      e.preventDefault();
      const m = middle(e.touches);
      zoomTo((pinch.zoom * spread(e.touches)) / pinch.spread, pinch.point, m.x, m.y);
    };
    const end = (e: TouchEvent) => {
      if (e.touches.length < 2) pinch = null;
    };
    const wheel = (e: WheelEvent) => {
      if (!e.ctrlKey || !pagesBox.current) return;
      e.preventDefault();
      zoomTo(zoom.current * Math.exp(-e.deltaY / 100), pointAt(e.clientX, e.clientY), e.clientX, e.clientY);
    };
    el.addEventListener("touchstart", start, { passive: true });
    el.addEventListener("touchmove", move, { passive: false });
    el.addEventListener("touchend", end);
    el.addEventListener("touchcancel", end);
    el.addEventListener("wheel", wheel, { passive: false });
    return () => {
      el.removeEventListener("touchstart", start);
      el.removeEventListener("touchmove", move);
      el.removeEventListener("touchend", end);
      el.removeEventListener("touchcancel", end);
      el.removeEventListener("wheel", wheel);
    };
  }, []);

  useEffect(() => {
    const key = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onClose]);

  return (
    <div class="pdf-viewer" role="dialog" aria-label={name}>
      <div class="toolbar">
        <button class="icon-button" title="Close" onClick={onClose}>
          <Icon name="close" />
        </button>
        <h2 class="title">{name}</h2>
        <button class="icon-button" title="Download" onClick={onDownload}>
          <Icon name="download" />
        </button>
      </div>
      <div class="pdf-scroll" ref={scroller}>
        {failed || !sizes ? (
          <p class="pdf-message">{failed ?? "Opening…"}</p>
        ) : (
          <div class="pdf-pages" ref={pagesBox}>
            {sizes.map((s, i) => (
              <div class="pdf-page" key={i} data-page={i} style={{ aspectRatio: `${s.width} / ${s.height}` }}>
                {images[i] ? (
                  <img src={images[i]!} alt={`Page ${i + 1} of ${sizes.length}`} />
                ) : (
                  images[i] === null && <span class="muted small">Page {i + 1} could not be drawn.</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
